import { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchQuery,
  graphql,
  useLazyLoadQuery,
  useMutation,
  useRelayEnvironment,
} from "react-relay";
import { Link } from "@tanstack/react-router";

import { useQueueLock } from "@app/components/account-queue/use-queue-lock";

import AccountDropzone from "@app/pages/statements/account-dropzone";
import type { DropzoneAccount } from "@app/pages/statements/account-dropzone";
import UploadZone from "@app/pages/statements/upload-zone";

import type { sourcesPage_Query } from "@app/queries/sourcesPage_Query.graphql";
import type { sourcesPage_OnboardMutation } from "@app/queries/sourcesPage_OnboardMutation.graphql";
import type { sourcesPage_SyncNowMutation } from "@app/queries/sourcesPage_SyncNowMutation.graphql";
import type { sourcesPage_CancelMutation } from "@app/queries/sourcesPage_CancelMutation.graphql";
import type { sourcesPage_DisconnectMutation } from "@app/queries/sourcesPage_DisconnectMutation.graphql";
import type { sourcesPage_TagsConfirmMutation } from "@app/queries/sourcesPage_TagsConfirmMutation.graphql";
import type { sourcesPage_SyncStatusQuery } from "@app/queries/sourcesPage_SyncStatusQuery.graphql";
import type { sourcesPage_SwitchoverMutation } from "@app/queries/sourcesPage_SwitchoverMutation.graphql";

// U15 Sources control room: every source on one page — per-account statement
// coverage timelines with upload dropzones (absorbing /statements' upload leg),
// the QBO connection with its sync controls (absorbing /qbo-reconciliation),
// the onboard flow + P&L tag confirm (U9), tie-out health (U4), and the
// review-queue / account-mapping entry points (U13/U14). Detail surfaces stay
// where they are (/statements registry, /sources/review, /sources/accounts) —
// this page composes and links, it does not duplicate.

const pageQuery = graphql`
  query sourcesPage_Query {
    qboConnectionStatus {
      connected
      realmId
      companyName
      connectUrl
    }
    qboPnlTags {
      qboAccountId
      name
      acctSubType
      pnlLine
      provisional
    }
    sourcesOverview {
      qboFrom
      qboTo
      unionActivatedAt
      accounts {
        kind
        qboMapped
        fileCount
        lastUploadAt
        witnessedCount
        qboSourcedCount
        bankAccount {
          id
          number
          nickname
        }
        statementRanges {
          from
          to
        }
      }
    }
    sourcingReview {
      totalCount
      countedSumCents
      uncountedSumCents
      run {
        status
      }
    }
  }
`;

const syncStatusQuery = graphql`
  query sourcesPage_SyncStatusQuery {
    qboSyncStatus {
      status
      mode
      phase
      rowsProcessed
      rowsTotal
      currentObject
      finishedAt
      errorMessage
      tieOut {
        qboAccountId
        name
        computed
        reported
        delta
        asOf
      }
    }
  }
`;

const onboardMutation = graphql`
  mutation sourcesPage_OnboardMutation(
    $importStartOn: String
    $confirmRetractCount: Int
  ) {
    qboSyncStart(
      mode: "onboard"
      importStartOn: $importStartOn
      confirmRetractCount: $confirmRetractCount
    ) {
      enqueued
      retractCount
      errors {
        message
      }
    }
  }
`;

const syncNowMutation = graphql`
  mutation sourcesPage_SyncNowMutation($mode: String) {
    qboSyncStart(mode: $mode) {
      enqueued
      errors {
        message
      }
    }
  }
`;

const cancelMutation = graphql`
  mutation sourcesPage_CancelMutation {
    qboSyncCancel {
      cancelled
      errors {
        message
      }
    }
  }
`;

const disconnectMutation = graphql`
  mutation sourcesPage_DisconnectMutation {
    qboConnectionDisconnect {
      ok
      errors {
        message
      }
    }
  }
`;

const switchoverMutation = graphql`
  mutation sourcesPage_SwitchoverMutation($action: String!) {
    sourcingSwitchover(action: $action) {
      ok
      preview {
        batchId
        matched
        claimed
        autoFilled
        flagged
        pending
        demoted
      }
      errors {
        message
      }
    }
  }
`;

const tagsConfirmMutation = graphql`
  mutation sourcesPage_TagsConfirmMutation($tags: [QboPnlTagInputObject!]!) {
    qboPnlTagsConfirm(tags: $tags) {
      ok
      errors {
        message
      }
    }
  }
`;

type SyncRun = NonNullable<
  sourcesPage_SyncStatusQuery["response"]["qboSyncStatus"]
>;
type Overview = NonNullable<sourcesPage_Query["response"]["sourcesOverview"]>;
type SourceAccount = Overview["accounts"][number];

const SYNC_POLL_MS = 1500;

// The onboard phase pipeline (U8). The sync-run row flips status "success" when
// the SYNCING leg commits while the later phases still run — "working" is
// therefore phase-driven for onboard runs, not status-driven.
const ONBOARD_PHASES = [
  "SYNCING",
  "MAPPING",
  "MATERIALIZING",
  "ATTRIBUTING",
  "CATEGORIZING",
  "DERIVING",
] as const;

const PNL_LINES = [
  "COGS",
  "S_AND_M",
  "R_AND_D",
  "G_AND_A",
  "TAXES",
  "EXCLUDED",
] as const;

const PNL_LINE_LABELS: Record<string, string> = {
  COGS: "Cost of goods sold",
  S_AND_M: "Sales & marketing",
  R_AND_D: "Research & development",
  G_AND_A: "General & administrative",
  TAXES: "Taxes",
  EXCLUDED: "Excluded from P&L",
  UNTAGGED: "Untagged",
};

const isOnboardWorking = (run: SyncRun | null) =>
  run != null &&
  run.mode === "onboard" &&
  run.phase != null &&
  run.phase !== "DONE" &&
  run.phase !== "FAILED";

// A run the user (or a dead worker) stopped — a clean terminal state, distinct
// from a genuine failure, that ends the "working" spinner and clears the gate.
const isInterrupted = (run: SyncRun) => run.status === "interrupted";

const isFailed = (run: SyncRun) =>
  run.status === "error" || run.phase === "FAILED";

const money = (dollars: number) =>
  dollars.toLocaleString(undefined, { style: "currency", currency: "USD" });

export default function SourcesPage() {
  const environment = useRelayEnvironment();
  const [refreshKey, setRefreshKey] = useState(0);
  // A sync already on the queue makes a second one pointless — it could only
  // wait behind the first and redo its work.
  const { isQueued, lockedTitle } = useQueueLock();
  const qboQueued = isQueued("QBO_SYNC");

  const data = useLazyLoadQuery<sourcesPage_Query>(
    pageQuery,
    {},
    { fetchKey: refreshKey, fetchPolicy: "store-and-network" },
  );

  const [commitOnboard, onboardInFlight] =
    useMutation<sourcesPage_OnboardMutation>(onboardMutation);
  const [commitSyncNow, syncNowInFlight] =
    useMutation<sourcesPage_SyncNowMutation>(syncNowMutation);
  const [commitDisconnect, disconnectInFlight] =
    useMutation<sourcesPage_DisconnectMutation>(disconnectMutation);
  const [commitCancel, cancelInFlight] =
    useMutation<sourcesPage_CancelMutation>(cancelMutation);

  const refresh = () => setRefreshKey((key) => key + 1);

  // --- Onboard start + retract-count confirmation (U8 contract) --------------
  const [importStartOn, setImportStartOn] = useState("");
  const [retractPrompt, setRetractPrompt] = useState<number | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [pendingStart, setPendingStart] = useState(false);

  // --- Sync status polling ----------------------------------------------------
  const [syncRun, setSyncRun] = useState<SyncRun | null>(null);

  const pollSyncStatus = useCallback(() => {
    fetchQuery<sourcesPage_SyncStatusQuery>(
      environment,
      syncStatusQuery,
      {},
      { fetchPolicy: "network-only" },
    ).subscribe({
      next: (response) => setSyncRun(response.qboSyncStatus ?? null),
    });
  }, [environment]);

  useEffect(() => {
    pollSyncStatus();
  }, [pollSyncStatus]);

  const working =
    pendingStart || syncRun?.status === "running" || isOnboardWorking(syncRun);

  useEffect(() => {
    if (!working) return;
    const id = setInterval(pollSyncStatus, SYNC_POLL_MS);
    return () => clearInterval(id);
  }, [working, pollSyncStatus]);

  // A watched run reaching a terminal state clears the placeholder and refreshes
  // the page data (coverage spans, tags, tie-out all move during a run).
  const phase = syncRun?.phase ?? null;
  useEffect(() => {
    if (syncRun == null) return;
    if (syncRun.status === "running" || isOnboardWorking(syncRun)) {
      setPendingStart(false);
      return;
    }
    if (phase === "DONE" || syncRun.status === "success" || isFailed(syncRun)) {
      setPendingStart(false);
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, syncRun?.status]);

  const startOnboard = (confirmRetractCount?: number) => {
    setStartError(null);
    setRetractPrompt(null);
    commitOnboard({
      variables: {
        importStartOn: importStartOn === "" ? null : importStartOn,
        confirmRetractCount: confirmRetractCount ?? null,
      },
      onCompleted: (response) => {
        const payload = response.qboSyncStart;
        if (payload?.enqueued) {
          setPendingStart(true);
          pollSyncStatus();
        } else if (payload?.retractCount != null) {
          // U8 count-naming confirmation: re-call echoing the exact count.
          setRetractPrompt(payload.retractCount);
        } else {
          setStartError(
            payload?.errors[0]?.message ?? "The import could not start.",
          );
        }
      },
    });
  };

  // Absorbed from the legacy reconciliation page: incremental "Sync now" and
  // "Full resync" (re-pull everything, clear what QBO no longer has).
  const startSync = (mode: string) => {
    setStartError(null);
    commitSyncNow({
      variables: { mode },
      onCompleted: (response) => {
        if (response.qboSyncStart?.enqueued) {
          setPendingStart(true);
          pollSyncStatus();
        } else {
          setStartError(
            response.qboSyncStart?.errors[0]?.message ??
              "The sync could not start.",
          );
        }
      },
    });
  };

  const disconnect = () => {
    if (
      !window.confirm(
        "Disconnect QuickBooks? You'll need to reconnect before syncing again.",
      )
    ) {
      return;
    }
    commitDisconnect({
      variables: {},
      onCompleted: () => refresh(),
    });
  };

  // Interrupt an in-flight (usually orphaned) sync so the page unsticks and the
  // account can be re-synced. Re-poll immediately to pick up the terminal state.
  const cancelSync = () => {
    setStartError(null);
    commitCancel({
      variables: {},
      onCompleted: () => {
        setPendingStart(false);
        pollSyncStatus();
      },
    });
  };

  const status = data.qboConnectionStatus;
  const overview = data.sourcesOverview;
  const review = data.sourcingReview;
  const busy =
    onboardInFlight || syncNowInFlight || disconnectInFlight || working;

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <header>
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold text-gray-900">Sources</h1>
          <div className="flex items-center gap-4">
            <Link
              to="/sources/review"
              className={`text-sm underline hover:no-underline ${
                review != null && review.totalCount > 0
                  ? "text-amber-700"
                  : "text-indigo-600"
              }`}
            >
              Review
              {review != null && review.totalCount > 0 && (
                <>
                  {" "}
                  ({review.totalCount.toLocaleString()} to decide ·{" "}
                  {money(review.countedSumCents / 100)} counted ·{" "}
                  {money(review.uncountedSumCents / 100)} not
                  {review.run?.status === "running" && " · applying decisions…"})
                </>
              )}
            </Link>
            <Link
              to="/sources/accounts"
              className="text-sm text-indigo-600 underline hover:no-underline"
            >
              Account mapping
            </Link>
            <Link
              to="/statements"
              className="text-sm text-indigo-600 underline hover:no-underline"
            >
              Statement files
            </Link>
          </div>
        </div>
        <p className="text-sm text-gray-500">
          {status.connected ? (
            <>
              QuickBooks connected —{" "}
              <span className="font-medium text-gray-700">
                {status.companyName ?? "QuickBooks"}
              </span>
              <span
                title="Matches “Company ID” in QuickBooks under the gear menu → Account and settings → Billing & subscription"
                className="ml-2 text-xs text-gray-400"
              >
                Company ID {status.realmId ?? "—"}
              </span>
            </>
          ) : (
            "Every place your ledger is sourced from: statement uploads per account, and QuickBooks."
          )}
        </p>
      </header>

      {/* QBO source: connect, onboard, sync controls, progress */}
      <section className="rounded border bg-white p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium text-gray-700">QuickBooks</h2>
          {status.connected && (
            <div className="flex gap-2">
              <button
                type="button"
                disabled={busy || qboQueued}
                onClick={() => startSync("incremental")}
                title={qboQueued ? lockedTitle : undefined}
                className="rounded border px-3 py-1.5 text-sm text-gray-700 disabled:opacity-50"
              >
                {qboQueued
                  ? "Already queued"
                  : working
                  ? "Syncing…"
                  : "Sync now"}
              </button>
              <button
                type="button"
                disabled={busy || qboQueued}
                onClick={() => startSync("full")}
                title={
                  qboQueued
                    ? lockedTitle
                    : "Re-pulls the complete QBO history (ignores the incremental cursor) and clears anything no longer in QBO. Use after reconnecting or if transactions look missing."
                }
                className="rounded border px-3 py-1.5 text-sm text-gray-500 disabled:opacity-50"
              >
                Full resync
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={disconnect}
                className="rounded border border-red-200 px-3 py-1.5 text-sm text-red-600 disabled:opacity-50"
              >
                {disconnectInFlight ? "Disconnecting…" : "Disconnect"}
              </button>
            </div>
          )}
        </div>

        {overview?.qboFrom != null && (
          <p className="text-xs text-gray-400">
            QBO coverage {overview.qboFrom} → {overview.qboTo} (synced-through
            follows the last synced record)
          </p>
        )}

        <div className="flex flex-wrap items-end gap-3">
          {!status.connected && status.connectUrl && (
            <a
              href={status.connectUrl}
              className="rounded bg-indigo-600 px-3 py-1.5 text-sm text-white"
            >
              Connect QuickBooks
            </a>
          )}
          {status.connected && (
            <>
              <label className="text-sm text-gray-600">
                <span className="block text-xs text-gray-400">
                  Import start (optional — records before it never enter the
                  ledger; the opening balance anchor carries them)
                </span>
                <input
                  type="date"
                  value={importStartOn}
                  onChange={(event) => setImportStartOn(event.target.value)}
                  className="mt-1 rounded border px-2 py-1 text-sm"
                />
              </label>
              <button
                type="button"
                disabled={busy}
                onClick={() => startOnboard()}
                className="rounded bg-indigo-600 px-3 py-1.5 text-sm text-white disabled:opacity-50"
              >
                {working ? "Importing…" : "Sync QuickBooks history"}
              </button>
            </>
          )}
        </div>

        {retractPrompt != null && (
          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Moving the import start to {importStartOn} removes{" "}
            <span className="font-semibold">
              {retractPrompt.toLocaleString()}
            </span>{" "}
            already-imported QuickBooks transaction
            {retractPrompt === 1 ? "" : "s"} from the ledger.
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => startOnboard(retractPrompt)}
                className="rounded border border-amber-300 px-2 py-1 text-xs text-amber-800 disabled:opacity-50"
              >
                Remove {retractPrompt.toLocaleString()} and continue
              </button>
              <button
                type="button"
                onClick={() => setRetractPrompt(null)}
                className="rounded px-2 py-1 text-xs text-amber-700 underline"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {startError && <p className="text-sm text-red-700">{startError}</p>}

        <OnboardProgress
          run={syncRun}
          pendingStart={pendingStart}
          canRetry={status.connected && !busy}
          onRetry={() => startOnboard()}
          onCancel={cancelSync}
          canceling={cancelInFlight}
        />
      </section>

      {/* U16 switchover: inactive both-source accounts only */}
      {status.connected && overview != null && (
        <SwitchoverSection overview={overview} busy={busy} onCommitted={refresh} />
      )}

      {/* Per-account coverage + uploads */}
      <SourceAccountsSection overview={overview ?? null} />

      {status.connected && (
        <PnlTagsSection
          tags={data.qboPnlTags}
          onConfirmed={refresh}
          busy={working}
        />
      )}

      {syncRun != null && syncRun.tieOut.length > 0 && (
        <section className="rounded border bg-white p-4">
          <h2 className="text-sm font-medium text-gray-700">Balance tie-out</h2>
          <p className="mb-2 text-xs text-gray-400">
            Computed running balance vs QuickBooks&apos; reported balance, per
            mapped account.
          </p>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-400">
                <th className="py-1 font-normal">Account</th>
                <th className="py-1 text-right font-normal">Computed</th>
                <th className="py-1 text-right font-normal">Reported</th>
                <th className="py-1 text-right font-normal">Delta</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {syncRun.tieOut.map((row) => (
                <tr key={row.qboAccountId}>
                  <td className="py-1 text-gray-800">
                    {row.name ?? row.qboAccountId}
                  </td>
                  <td className="py-1 text-right tabular-nums text-gray-600">
                    {money(row.computed)}
                  </td>
                  <td className="py-1 text-right tabular-nums text-gray-600">
                    {money(row.reported)}
                  </td>
                  <td
                    className={`py-1 text-right tabular-nums ${
                      row.delta === 0 ? "text-gray-400" : "text-amber-700"
                    }`}
                  >
                    {money(row.delta)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}

// --- U16 switchover card -------------------------------------------------------

type SwitchoverPreview = NonNullable<
  NonNullable<
    sourcesPage_SwitchoverMutation["response"]["sourcingSwitchover"]
  >["preview"]
>;

// The one-time first-union activation for an existing both-source account:
// preview the counts (dry run, no writes), then commit — after which the
// account lives under the automatic union lifecycle and this card disappears.
// Only rendered for a connected, INACTIVE account that has both a QBO staging
// corpus and statement files (the mutation's preflight stays authoritative).
function SwitchoverSection({
  overview,
  busy,
  onCommitted,
}: {
  overview: Overview;
  busy: boolean;
  onCommitted: () => void;
}) {
  const [preview, setPreview] = useState<SwitchoverPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [commitSwitchover, inFlight] =
    useMutation<sourcesPage_SwitchoverMutation>(switchoverMutation);

  const eligible =
    overview.unionActivatedAt == null &&
    overview.qboFrom != null &&
    overview.accounts.some((account) => account.fileCount > 0);

  if (!eligible) return null;

  const run = (action: "PREVIEW" | "COMMIT") => {
    setError(null);
    commitSwitchover({
      variables: { action },
      onCompleted: (response) => {
        const payload = response.sourcingSwitchover;
        if (!payload?.ok) {
          setError(
            payload?.errors[0]?.message ?? "The switchover could not run.",
          );
          return;
        }
        if (action === "PREVIEW") {
          setPreview(payload.preview ?? null);
        } else {
          setPreview(null);
          onCommitted();
        }
      },
    });
  };

  const confirmCommit = () => {
    if (
      !window.confirm(
        "Activate the QuickBooks union? QBO transactions outside your statement coverage " +
          "enter the ledger, matches bind to their bank twins, and discrepancies land in the " +
          "review queue. This runs once and is previewed above.",
      )
    ) {
      return;
    }
    run("COMMIT");
  };

  return (
    <section className="rounded border border-amber-200 bg-amber-50 p-4 space-y-3">
      <div>
        <h2 className="text-sm font-medium text-amber-900">
          QuickBooks union not activated
        </h2>
        <p className="text-xs text-amber-800">
          This account has both statement uploads and a QuickBooks feed, but
          they are not unified yet. Map your accounts first (Account mapping),
          then preview what the first union run would do and activate it. The
          flagged backlog is reviewed afterwards in the review queue.
        </p>
      </div>

      {preview != null && (
        <div className="rounded border border-amber-200 bg-white px-3 py-2 text-sm text-gray-700">
          <span className="font-medium">First union preview:</span>{" "}
          {preview.matched.toLocaleString()} matched (
          {preview.claimed.toLocaleString()} claimed),{" "}
          {preview.autoFilled.toLocaleString()} auto-filled outside coverage,{" "}
          <span className={preview.flagged > 0 ? "text-amber-700" : ""}>
            {preview.flagged.toLocaleString()} flagged for review
          </span>
          , {preview.pending.toLocaleString()} pending,{" "}
          {preview.demoted.toLocaleString()} demoted.
        </div>
      )}

      {error && <p className="text-sm text-red-700">{error}</p>}

      <div className="flex gap-2">
        <button
          type="button"
          disabled={busy || inFlight}
          onClick={() => run("PREVIEW")}
          className="rounded border border-amber-300 px-3 py-1.5 text-sm text-amber-900 disabled:opacity-50"
        >
          {inFlight ? "Working…" : "Preview first union"}
        </button>
        <button
          type="button"
          disabled={busy || inFlight || preview == null}
          title={
            preview == null ? "Preview first — the commit runs the same counts" : undefined
          }
          onClick={confirmCommit}
          className="rounded bg-amber-600 px-3 py-1.5 text-sm text-white disabled:opacity-50"
        >
          Activate union
        </button>
      </div>
    </section>
  );
}

// --- Per-account source rows -------------------------------------------------

const dayMs = 24 * 60 * 60 * 1000;
const parseDay = (iso: string) => Date.parse(iso);

// Every canonical account with its day-grain coverage timeline (statement spans
// + the tenant QBO span when a feed maps here), upload recency, provenance
// counts, and a dropzone. All rows share one date domain so gaps line up.
function SourceAccountsSection({ overview }: { overview: Overview | null }) {
  const accounts = overview?.accounts ?? [];
  const qboSpan =
    overview?.qboFrom != null && overview.qboTo != null
      ? ([parseDay(overview.qboFrom), parseDay(overview.qboTo)] as const)
      : null;

  const domain = useMemo(() => {
    const points: number[] = [];
    for (const account of accounts) {
      for (const range of account.statementRanges) {
        points.push(parseDay(range.from), parseDay(range.to));
      }
      if (account.qboMapped && qboSpan) points.push(...qboSpan);
    }
    if (points.length === 0) return null;
    return [Math.min(...points), Math.max(...points)] as const;
  }, [accounts, qboSpan]);

  const dropzoneAccounts: DropzoneAccount[] = accounts.map((account) => ({
    number: account.bankAccount.number,
    nickname: account.bankAccount.nickname,
    kind: account.kind,
  }));

  return (
    <section className="rounded border bg-white p-4 space-y-3">
      <div className="flex items-baseline justify-between">
        <div>
          <h2 className="text-sm font-medium text-gray-700">
            Statement accounts
          </h2>
          <p className="text-xs text-gray-400">
            Day-grain coverage per account —{" "}
            <span className="text-indigo-600">statements</span>
            {qboSpan && (
              <>
                {" · "}
                <span className="text-emerald-600">QBO feed</span>
              </>
            )}
            . Holes in a bar are coverage gaps.
          </p>
        </div>
        {domain && (
          <span className="text-xs tabular-nums text-gray-400">
            {new Date(domain[0]).toISOString().slice(0, 10)} →{" "}
            {new Date(domain[1]).toISOString().slice(0, 10)}
          </span>
        )}
      </div>

      <UploadZone />

      {accounts.length === 0 ? (
        <p className="text-sm text-gray-500">
          No source accounts yet — upload a statement export above or connect
          QuickBooks.
        </p>
      ) : (
        <ul className="divide-y">
          {accounts.map((account) => (
            <SourceAccountRow
              key={account.bankAccount.id}
              account={account}
              qboSpan={account.qboMapped ? qboSpan : null}
              domain={domain}
              dropzoneAccounts={dropzoneAccounts}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function SourceAccountRow({
  account,
  qboSpan,
  domain,
  dropzoneAccounts,
}: {
  account: SourceAccount;
  qboSpan: readonly [number, number] | null;
  domain: readonly [number, number] | null;
  dropzoneAccounts: DropzoneAccount[];
}) {
  return (
    <li className="py-3 space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="min-w-0">
          <span className="font-medium text-gray-800">
            {account.bankAccount.nickname}
          </span>
          <span className="ml-2 text-xs text-gray-400">
            {account.kind}
            {account.qboMapped && " · QBO feed mapped"}
          </span>
        </div>
        <div className="text-xs text-gray-400">
          {account.fileCount.toLocaleString()} file
          {account.fileCount === 1 ? "" : "s"}
          {account.lastUploadAt != null &&
            ` · last upload ${new Date(account.lastUploadAt).toLocaleDateString()}`}
          {" · "}
          {account.witnessedCount.toLocaleString()} witnessed ·{" "}
          {account.qboSourcedCount.toLocaleString()} QBO-sourced
        </div>
      </div>

      <CoverageTimeline
        ranges={account.statementRanges}
        qboSpan={qboSpan}
        domain={domain}
      />

      <AccountDropzone
        account={{
          number: account.bankAccount.number,
          nickname: account.bankAccount.nickname,
          kind: account.kind,
        }}
        accounts={dropzoneAccounts}
      />
    </li>
  );
}

// Two thin lanes on a shared date axis: statement coverage windows (indigo) and
// the QBO span (emerald). The gray track showing through IS the gap.
function CoverageTimeline({
  ranges,
  qboSpan,
  domain,
}: {
  ranges: SourceAccount["statementRanges"];
  qboSpan: readonly [number, number] | null;
  domain: readonly [number, number] | null;
}) {
  if (!domain || (ranges.length === 0 && !qboSpan)) {
    return <p className="text-xs text-gray-400">No coverage yet.</p>;
  }

  const [min, max] = domain;
  const span = Math.max(max - min, dayMs);
  const bar = (from: number, to: number) => ({
    left: `${((from - min) / span) * 100}%`,
    // A single-day window still renders a visible sliver.
    width: `${Math.max(((to - from + dayMs) / span) * 100, 0.4)}%`,
  });

  return (
    <div className="space-y-0.5">
      <div className="relative h-2 rounded bg-gray-100">
        {ranges.map((range) => (
          <div
            key={`${range.from}-${range.to}`}
            title={`Statements ${range.from} → ${range.to}`}
            className="absolute top-0 h-full rounded bg-indigo-400"
            style={bar(parseDay(range.from), parseDay(range.to))}
          />
        ))}
      </div>
      {qboSpan && (
        <div className="relative h-2 rounded bg-gray-100">
          <div
            title="QBO coverage"
            className="absolute top-0 h-full rounded bg-emerald-400"
            style={bar(qboSpan[0], qboSpan[1])}
          />
        </div>
      )}
    </div>
  );
}

// The escape hatch from a stuck "Importing…" state — enabled while everything
// else on the card is disabled (busy). Marks the in-flight run terminal so the
// page unsticks and the account can be re-synced; see Qbo::CancelSync.
function InterruptButton({
  onCancel,
  canceling,
}: {
  onCancel: () => void;
  canceling: boolean;
}) {
  return (
    <button
      type="button"
      disabled={canceling}
      onClick={onCancel}
      title="Stop this import. Use it if the sync is stuck. You can start it again afterwards."
      className="shrink-0 rounded border border-red-200 px-2 py-1 text-xs text-red-600 disabled:opacity-50"
    >
      {canceling ? "Stopping…" : "Interrupt"}
    </button>
  );
}

// Phase pipeline for onboard runs; plain progress for other modes. A FAILED run
// shows the error and a retry button — onboard is convergent, so retry just
// re-fires the mutation.
function OnboardProgress({
  run,
  pendingStart,
  canRetry,
  onRetry,
  onCancel,
  canceling,
}: {
  run: SyncRun | null;
  pendingStart: boolean;
  canRetry: boolean;
  onRetry: () => void;
  onCancel: () => void;
  canceling: boolean;
}) {
  if (
    pendingStart &&
    (run == null || (run.status !== "running" && !isOnboardWorking(run)))
  ) {
    return (
      <div className="flex items-center gap-2 text-sm text-indigo-800">
        <span className="h-2 w-2 animate-pulse rounded-full bg-indigo-500" />
        Starting import…
      </div>
    );
  }

  if (run == null) return null;

  if (isInterrupted(run)) {
    return (
      <div className="flex items-center justify-between gap-4 rounded border border-amber-200 bg-amber-50 px-3 py-2">
        <div className="text-sm text-amber-800">
          <span className="font-medium">Import stopped.</span>{" "}
          {run.errorMessage ?? "You interrupted the sync."}
        </div>
        <button
          type="button"
          disabled={!canRetry}
          onClick={onRetry}
          className="shrink-0 rounded border border-amber-300 px-3 py-1.5 text-sm text-amber-800 disabled:opacity-50"
        >
          Start again
        </button>
      </div>
    );
  }

  if (isFailed(run)) {
    return (
      <div className="flex items-center justify-between gap-4 rounded border border-red-200 bg-red-50 px-3 py-2">
        <div className="text-sm text-red-800">
          <span className="font-medium">Import failed.</span>{" "}
          {run.errorMessage ?? "Something went wrong."}
        </div>
        <button
          type="button"
          disabled={!canRetry}
          onClick={onRetry}
          className="shrink-0 rounded border border-red-300 px-3 py-1.5 text-sm text-red-700 disabled:opacity-50"
        >
          Retry import
        </button>
      </div>
    );
  }

  const working = run.status === "running" || isOnboardWorking(run);

  if (working && run.mode === "onboard") {
    const currentIndex = ONBOARD_PHASES.indexOf(
      (run.phase ?? "SYNCING") as (typeof ONBOARD_PHASES)[number],
    );
    return (
      <div className="space-y-2">
        <div className="flex items-start justify-between gap-3">
          <ol className="flex flex-wrap gap-1 text-xs">
            {ONBOARD_PHASES.map((name, index) => (
              <li
                key={name}
                className={`rounded px-2 py-1 ${
                  index < currentIndex
                    ? "bg-green-50 text-green-700"
                    : index === currentIndex
                      ? "bg-indigo-100 font-medium text-indigo-800"
                      : "bg-gray-50 text-gray-400"
                }`}
              >
                {name.charAt(0) + name.slice(1).toLowerCase()}
              </li>
            ))}
          </ol>
          <InterruptButton onCancel={onCancel} canceling={canceling} />
        </div>
        {run.phase === "SYNCING" && (
          <p className="text-xs text-indigo-700">
            {run.rowsTotal == null
              ? "Counting QuickBooks records…"
              : `${run.rowsProcessed.toLocaleString()} / ${run.rowsTotal.toLocaleString()} rows`}
            {run.currentObject && ` · ${run.currentObject}`}
          </p>
        )}
      </div>
    );
  }

  if (working) {
    return (
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-indigo-800">
          Syncing… {run.rowsProcessed.toLocaleString()} rows
          {run.currentObject && ` · ${run.currentObject}`}
        </p>
        <InterruptButton onCancel={onCancel} canceling={canceling} />
      </div>
    );
  }

  if (run.phase === "DONE" || run.status === "success") {
    return (
      <p className="text-sm text-gray-600">
        Last sync complete
        {run.finishedAt != null &&
          ` ${new Date(run.finishedAt).toLocaleString()}`}{" "}
        — {run.rowsProcessed.toLocaleString()} rows.{" "}
        <Link to="/dashboard" className="text-indigo-600 underline">
          View dashboard
        </Link>
      </p>
    );
  }

  return null;
}

// The P&L-tag confirm screen (U6 surface): heuristic tags grouped by P&L line,
// provisional markers on unconfirmed prefills, per-row line override, one
// confirm action persisting every tagged account.
function PnlTagsSection({
  tags,
  onConfirmed,
  busy,
}: {
  tags: sourcesPage_Query["response"]["qboPnlTags"];
  onConfirmed: () => void;
  busy: boolean;
}) {
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [commitTags, tagsInFlight] =
    useMutation<sourcesPage_TagsConfirmMutation>(tagsConfirmMutation);

  const effectiveLine = useCallback(
    (tag: (typeof tags)[number]) => {
      const edited = edits[tag.qboAccountId];
      if (edited !== undefined) return edited === "" ? null : edited;
      return tag.pnlLine ?? null;
    },
    [edits],
  );

  const groups = useMemo(() => {
    const byLine = new Map<string, typeof tags>();
    for (const tag of tags) {
      const line = effectiveLine(tag) ?? "UNTAGGED";
      byLine.set(line, [...(byLine.get(line) ?? []), tag]);
    }
    const order = [...PNL_LINES, "UNTAGGED"];
    return order
      .filter((line) => byLine.has(line))
      .map((line) => ({ line, entries: byLine.get(line)! }));
  }, [tags, effectiveLine]);

  const provisionalCount = tags.filter(
    (tag) => tag.provisional && effectiveLine(tag) != null,
  ).length;

  const confirm = () => {
    setConfirmError(null);
    const payload = tags
      .map((tag) => ({
        qboAccountId: tag.qboAccountId,
        pnlLine: effectiveLine(tag),
      }))
      .filter(
        (tag): tag is { qboAccountId: string; pnlLine: string } =>
          tag.pnlLine != null,
      );
    commitTags({
      variables: { tags: payload },
      onCompleted: (response) => {
        if (response.qboPnlTagsConfirm?.ok) {
          setEdits({});
          onConfirmed();
        } else {
          setConfirmError(
            response.qboPnlTagsConfirm?.errors[0]?.message ??
              "Tags could not be saved.",
          );
        }
      },
    });
  };

  if (tags.length === 0) {
    return (
      <section className="rounded border bg-white p-4">
        <h2 className="text-sm font-medium text-gray-700">P&amp;L line tags</h2>
        <p className="text-sm text-gray-500">
          No chart of accounts yet — run a sync first.
        </p>
      </section>
    );
  }

  return (
    <section className="rounded border bg-white p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-medium text-gray-700">
            P&amp;L line tags
          </h2>
          <p className="text-xs text-gray-400">
            How each QuickBooks account rolls into your P&amp;L (drives CAC cost
            centres).{" "}
            {provisionalCount > 0 &&
              `${provisionalCount.toLocaleString()} suggested tag${provisionalCount === 1 ? "" : "s"} awaiting confirmation.`}
          </p>
        </div>
        <button
          type="button"
          disabled={busy || tagsInFlight}
          onClick={confirm}
          className="rounded bg-indigo-600 px-3 py-1.5 text-sm text-white disabled:opacity-50"
        >
          {tagsInFlight ? "Saving…" : "Confirm tags"}
        </button>
      </div>
      {confirmError && <p className="text-sm text-red-700">{confirmError}</p>}

      {groups.map(({ line, entries }) => (
        <div key={line}>
          <h3 className="border-b pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
            {PNL_LINE_LABELS[line] ?? line}
          </h3>
          <ul className="divide-y">
            {entries.map((tag) => (
              <li
                key={tag.qboAccountId}
                className="flex items-center justify-between gap-3 py-1.5 text-sm"
              >
                <span className="min-w-0 truncate text-gray-800">
                  {tag.name}
                  {tag.acctSubType && (
                    <span className="ml-2 text-xs text-gray-400">
                      {tag.acctSubType}
                    </span>
                  )}
                  {tag.provisional && effectiveLine(tag) != null && (
                    <span className="ml-2 rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-700">
                      suggested
                    </span>
                  )}
                </span>
                <select
                  value={effectiveLine(tag) ?? ""}
                  onChange={(event) =>
                    setEdits((previous) => ({
                      ...previous,
                      [tag.qboAccountId]: event.target.value,
                    }))
                  }
                  className="shrink-0 rounded border px-2 py-1 text-xs text-gray-700"
                >
                  <option value="">—</option>
                  {PNL_LINES.map((option) => (
                    <option key={option} value={option}>
                      {PNL_LINE_LABELS[option]}
                    </option>
                  ))}
                </select>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}
